import type { BoPhanSuDungCCDC, BoPhanSuDungCCDCInput } from '@/types/schemas';
import { ConfirmationDialog, ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog } from './components';
import { useCRUD, useForms } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 20;

export default function BoPhanSuDungCCDCPage() {
  const {
    data: boPhanSuDungCCDCs,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    isLoading,
    searchData
  } = useCRUD<BoPhanSuDungCCDC, BoPhanSuDungCCDCInput>({
    endpoint: QUERY_KEYS.BO_PHAN_SU_DUNG_CCDC,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<BoPhanSuDungCCDC>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const displayData = hasFilterParams ? searchState.searchResults : boPhanSuDungCCDCs || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const columns = getDataTableColumns();

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: columns
    }
  ];

  const handleAddBoPhanSuDungCCDC = async (data: BoPhanSuDungCCDCInput) => {
    await addItem(data);
    await refreshData();
  };

  const handleUpdateBoPhanSuDungCCDC = async (data: BoPhanSuDungCCDCInput) => {
    if (selectedObj) {
      await updateItem(selectedObj.uuid, data);
      await refreshData();
    }
  };

  const handleSubmit = async (data: BoPhanSuDungCCDCInput) => {
    try {
      if (formMode === 'add') {
        await handleAddBoPhanSuDungCCDC(data);
      } else if (formMode === 'edit' && selectedObj) {
        await handleUpdateBoPhanSuDungCCDC(data);
      }
      handleCloseForm();
      clearSelection();
    } catch (err: unknown) {
      console.error('Error submitting form:', err);
    }
  };

  return (
    <div className='flex h-full min-h-screen w-full min-w-0 max-w-full flex-col lg:overflow-hidden'>
      <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
        <ActionBar
          onAddClick={handleAddClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          onCopyClick={handleCopyClick}
          onViewClick={handleViewClick}
          onRefreshClick={async () => {
            await refreshData();
            if (selectedObj?.uuid) {
              await fetchDetail(selectedObj.uuid);
            }
          }}
        />

        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <FormDialog
          open={showForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
          formMode={formMode}
        />
      )}

      {showDelete && (
        <ConfirmationDialog
          onClose={handleCloseDelete}
          onConfirm={async () => {
            if (selectedObj) {
              await deleteItem(selectedObj.uuid);
              await refreshData();
              handleCloseDelete();
              clearSelection();
            }
          }}
        />
      )}
    </div>
  );
}
